// src/services/maps/events.js
// Centralized map event handling.
// Components subscribe to typed events via addEventListener on the map —
// never binding raw Leaflet events in component code.
//
// Emits CustomEvents on window so non-React services can also listen.
// All handlers are registered here and cleaned up via detachMapEvents().

import { ZOOM_THRESHOLDS } from './config.js';

// ── Registered handler cleanup functions ──────────────────────────
const _cleanups = [];

export function attachMapEvents(map, callbacks = {}) {
  const {
    onMove,          // ({ lat, lon, zoom, bounds }) → void
    onZoom,          // ({ zoom }) → void
    onClickEmpty,    // ({ lat, lon }) → void — tap on map background
    onZoomThreshold, // ({ above, threshold }) → void — crossed SHOW_CLUSTERS
  } = callbacks;

  let _prevZoom = map.getZoom();

  // ── Move end (pan + zoom settled) ────────────────────────────────
  const onMoveEnd = () => {
    const c      = map.getCenter();
    const zoom   = map.getZoom();
    const bounds = map.getBounds();
    const payload = {
      lat:    c.lat,
      lon:    c.lng,
      zoom,
      bounds: {
        n: bounds.getNorth(),
        s: bounds.getSouth(),
        e: bounds.getEast(),
        w: bounds.getWest(),
      },
    };
    onMove?.(payload);
    window.dispatchEvent(new CustomEvent('na-map-moved', { detail: payload }));
  };

  // ── Zoom ──────────────────────────────────────────────────────────
  const onZoomEnd = () => {
    const zoom = map.getZoom();
    onZoom?.({ zoom });
    window.dispatchEvent(new CustomEvent('na-map-zoom', { detail: { zoom } }));

    // Detect threshold crossings
    if (onZoomThreshold) {
      const t = ZOOM_THRESHOLDS.SHOW_CLUSTERS;
      if (_prevZoom < t && zoom >= t) onZoomThreshold({ above: true,  threshold: t });
      if (_prevZoom >= t && zoom < t) onZoomThreshold({ above: false, threshold: t });
    }
    _prevZoom = zoom;
  };

  // ── Click on empty map ────────────────────────────────────────────
  const onMapClick = (e) => {
    onClickEmpty?.({ lat: e.latlng.lat, lon: e.latlng.lng });
    window.dispatchEvent(new CustomEvent('na-map-tap', {
      detail: { lat: e.latlng.lat, lon: e.latlng.lng },
    }));
  };

  map.on('moveend',  onMoveEnd);
  map.on('zoomend',  onZoomEnd);
  map.on('click',    onMapClick);

  // ── Register cleanup ──────────────────────────────────────────────
  const cleanup = () => {
    map.off('moveend',  onMoveEnd);
    map.off('zoomend',  onZoomEnd);
    map.off('click',    onMapClick);
  };
  _cleanups.push(cleanup);
  return cleanup;
}

export function detachMapEvents(map) {
  _cleanups.forEach((fn) => { try { fn(); } catch {} });
  _cleanups.length = 0;
}

// ── Programmatic viewport events ─────────────────────────────────
// Fired by other services to tell the map to change view.
// The map hook listens for these and calls MapManager methods.
export function emitFlyTo(lat, lon, zoom) {
  window.dispatchEvent(new CustomEvent('na-map-fly-to', { detail: { lat, lon, zoom } }));
}

export function emitFitBounds(bounds) {
  window.dispatchEvent(new CustomEvent('na-map-fit-bounds', { detail: { bounds } }));
}

export function emitHighlightItem(type, id) {
  window.dispatchEvent(new CustomEvent('na-map-highlight', { detail: { type, id } }));
}
